import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MailOpen, Trash2, LogOut, Inbox } from "lucide-react";
import { api, formatApiError, API } from "@/lib/api";
import { ContentEditor } from "@/components/admin/ContentEditor";

const ADMIN_SECTIONS = [
  { id: "editor-header", label: "Header" },
  { id: "editor-colors", label: "Colours" },
  { id: "editor-hero", label: "Hero" },
  { id: "editor-marquee", label: "Ribbon" },
  { id: "editor-headings", label: "Headings" },
  { id: "editor-bio", label: "Bio" },
  { id: "editor-services", label: "Services" },
  { id: "editor-resume-card", label: "Résumé & Photos" },
  { id: "editor-credits", label: "Credits" },
  { id: "editor-training", label: "Training" },
  { id: "editor-testimonials", label: "Testimonials" },
  { id: "editor-resources", label: "Resources" },
  { id: "editor-contact", label: "Contact" },
  { id: "editor-footer", label: "Footer" },
];

const triggerSave = () => window.dispatchEvent(new CustomEvent("cms-save"));

export default function Admin() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [unread, setUnread] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [view, setView] = useState("inbox");

  const loadMessages = async () => {
    try {
      const { data } = await api.get("/admin/messages");
      setMessages(data.messages);
      setUnread(data.unread);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) loadMessages();
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
    setMessages([]);
  };

  const toggleRead = async (id) => {
    const { data } = await api.patch(`/admin/messages/${id}/read`);
    setMessages((ms) => ms.map((m) => (m.id === id ? { ...m, read: data.read } : m)));
    setUnread((u) => (data.read ? u - 1 : u + 1));
  };

  const remove = async (id) => {
    await api.delete(`/admin/messages/${id}`);
    setMessages((ms) => ms.filter((m) => m.id !== id));
    toast.success("Message deleted");
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center" data-testid="admin-loading">
        <p className="text-sm uppercase tracking-[0.3em] text-charcoal/50">Loading…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center px-6" data-testid="admin-login-page">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md border border-champagne bg-ivory p-10 md:p-12"
        >
          <p className="overline-label">Private</p>
          <h1 className="mt-4 font-serif text-4xl font-medium text-burgundy">Studio Login</h1>
          <form onSubmit={login} className="mt-10 space-y-8" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="overline-label">Email</label>
              <input
                id="admin-email"
                type="email"
                required
                data-testid="admin-login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="editorial-input mt-2"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="overline-label">Password</label>
              <input
                id="admin-password"
                type="password"
                required
                data-testid="admin-login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="editorial-input mt-2"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p className="text-sm text-burgundy" data-testid="admin-login-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit-button"
              className="w-full rounded-full bg-burgundy px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.3em] text-ivory hover:bg-charcoal transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <a href="/" data-testid="admin-back-home-link" className="mt-8 inline-block text-[11px] uppercase tracking-[0.25em] text-charcoal/50 hover:text-burgundy transition-colors">
            ← Back to site
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory" data-testid="admin-inbox-page">
      <header className="border-b border-champagne bg-ivory">
        <div className="mx-auto max-w-5xl px-6 md:px-12 h-20 flex items-center justify-between">
          <div>
            <p className="font-serif text-xl font-semibold text-burgundy">Inquiries</p>
            <p className="text-[10px] uppercase tracking-[0.3em] text-charcoal/50" data-testid="admin-unread-count">
              {unread} unread
            </p>
          </div>
          <div className="flex items-center gap-6">
            <a href="/" data-testid="admin-view-site-link" className="text-[11px] uppercase tracking-[0.25em] text-charcoal/60 hover:text-burgundy transition-colors">
              View site
            </a>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-burgundy hover:text-charcoal transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 md:px-12 pt-8">
        <div className="flex gap-2" data-testid="admin-view-tabs">
          {[{ id: "inbox", label: `Inquiries${unread ? ` (${unread})` : ""}` }, { id: "content", label: "Site Content" }].map((t) => (
            <button
              key={t.id}
              onClick={() => setView(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.25em] border transition-colors duration-300 ${
                view === t.id
                  ? "bg-burgundy text-ivory border-burgundy"
                  : "bg-ivory text-charcoal/60 border-champagne hover:text-burgundy"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </main>

      {view === "inbox" ? (
      <main className="mx-auto max-w-5xl px-6 md:px-12 py-12" data-testid="admin-inbox">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-center" data-testid="admin-inbox-empty">
            <Inbox size={40} strokeWidth={1} className="text-champagne" />
            <p className="mt-6 font-serif text-2xl text-burgundy">No inquiries yet</p>
            <p className="mt-2 text-sm text-charcoal/60">Inquiries from your contact form will appear here.</p>
          </div>
        ) : (
          <ul className="divide-y divide-champagne border-y border-champagne">
            {messages.map((m) => (
              <li key={m.id} className="py-6" data-testid={`admin-message-${m.id}`}>
                <div className="flex items-start justify-between gap-6">
                  <button
                    onClick={() => {
                      setExpanded(expanded === m.id ? null : m.id);
                      if (!m.read) toggleRead(m.id);
                    }}
                    data-testid={`admin-message-toggle-${m.id}`}
                    className="flex-1 text-left"
                  >
                    <div className="flex items-center gap-3">
                      {m.read ? (
                        <MailOpen size={16} className="text-champagne shrink-0" />
                      ) : (
                        <Mail size={16} className="text-burgundy shrink-0" />
                      )}
                      <p className={`font-serif text-xl ${m.read ? "text-charcoal/70" : "text-burgundy font-semibold"}`}>
                        {m.name}
                      </p>
                      {!m.read && <span className="h-2 w-2 rounded-full bg-rose" data-testid={`admin-message-unread-dot-${m.id}`} />}
                    </div>
                    <p className="mt-1 text-sm text-charcoal/60">
                      {m.email}
                      {m.position ? ` · ${m.position}` : ""}
                      {m.production ? ` · ${m.production}` : ""}
                      {m.project_type ? ` · ${m.project_type}` : ""} ·{" "}
                      {new Date(m.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </p>
                  </button>
                  <button
                    onClick={() => remove(m.id)}
                    data-testid={`admin-message-delete-${m.id}`}
                    className="text-charcoal/40 hover:text-burgundy transition-colors"
                    aria-label="Delete message"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                {expanded === m.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="mt-4 ml-7 max-w-2xl"
                  >
                    <p className="whitespace-pre-wrap text-base leading-relaxed text-charcoal/80 border-l-2 border-champagne pl-6">
                      {m.message}
                    </p>
                    {m.script_file && (
                      <a
                        href={`${API}/files/enquiries/${m.script_file}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`admin-message-script-${m.id}`}
                        className="mt-4 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-burgundy hover:text-charcoal transition-colors"
                      >
                        Download script — {m.script_name}
                      </a>
                    )}
                    <button
                      onClick={() => toggleRead(m.id)}
                      data-testid={`admin-message-markread-${m.id}`}
                      className="mt-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-rose hover:text-burgundy transition-colors"
                    >
                      {m.read ? "Mark as unread" : "Mark as read"}
                    </button>
                  </motion.div>
                )}
              </li>
            ))}
          </ul>
        )}
      </main>
      ) : (
        <div className="mx-auto max-w-7xl px-6 md:px-12 py-12 flex gap-10">
          <aside className="hidden lg:block w-48 shrink-0">
            <nav className="sticky top-8 space-y-1" data-testid="admin-directory">
              <p className="px-3 pb-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-rose">Directory</p>
              <button
                onClick={triggerSave}
                data-testid="dir-save-top"
                className="mb-4 w-full rounded-full bg-burgundy px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-ivory hover:bg-charcoal transition-colors"
              >
                Save changes
              </button>
              {ADMIN_SECTIONS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth" })}
                  data-testid={`dir-${s.id}`}
                  className="block w-full text-left px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-charcoal/60 hover:text-burgundy hover:bg-champagne/20 transition-colors"
                >
                  {s.label}
                </button>
              ))}
              <button
                onClick={triggerSave}
                data-testid="dir-save-bottom"
                className="mt-4 w-full rounded-full bg-burgundy px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-ivory hover:bg-charcoal transition-colors"
              >
                Save changes
              </button>
            </nav>
          </aside>
          <div className="flex-1 min-w-0">
            <ContentEditor />
          </div>
        </div>
      )}
    </div>
  );
}
